import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { getCurrentUser, signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { authApi, profileApi, tokenStorage, ApiError, AUTH_EXPIRED_EVENT, clearInFlightGets, isOfflineError } from '../lib/api'
import { readProfileSnapshot, writeProfileSnapshot, clearProfileSnapshot } from '../lib/profileSnapshot'
import { clearActivity, isInactivityExpired, recordActivity } from '../lib/sessionActivity'
import i18n from '../i18n/config'
import type { User, AuthMethod } from '../types'

// Name of the SW runtime cache that holds authenticated GET responses
// (/profile, /playlists, /bookmarks, …). Must be kept in sync with
// vite.config.ts's runtimeCaching rule. We invalidate it on logout and on
// AUTH_EXPIRED so the next user on the same browser doesn't see the previous
// user's PII while their own background SWR fetch is still in flight.
const API_SWR_CACHE_NAME = 'qlearn-api-swr-v2'

// caches API isn't always available (e.g. SSR / older browsers / private
// browsing on iOS Safari < 11). Wrap the deletion in a guarded best-effort
// call so a missing API never breaks logout. Async but non-blocking — the
// caller can fire-and-forget; the next protected route render handles the
// auth state separately.
async function invalidateApiCache(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return
    await caches.delete(API_SWR_CACHE_NAME)
  } catch {
    /* best-effort — never block logout on a cache failure */
  }
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  login: (identifier: string, method: AuthMethod) => Promise<string>
  verifyOTP: (identifier: string, code: string, session: string, method: AuthMethod) => Promise<void>
  loginWithGoogle: () => Promise<void>
  register: (data: { name: string; email?: string; phoneNumber?: string }) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  // setUser exists so endpoints that already return the full updated profile
  // (e.g. POST /games/sessions) can push it straight into auth state without
  // forcing an extra GET /profile roundtrip via refreshUser.
  setUser: (user: User) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Single write path for "we have a confirmed profile": update state AND
  // refresh the offline snapshot so the next offline boot can hydrate.
  const applyUser = useCallback((userData: User) => {
    setUser(userData)
    writeProfileSnapshot(userData)
  }, [])

  const checkAuth = useCallback(async () => {
    // Rolling-inactivity gate on app boot. If the last recorded API activity
    // was more than 24 h ago, treat the session as expired even if Cognito's
    // refresh token would still validate. Drop tokens + activity record so
    // the rest of this function reports "not authenticated" and the router
    // bounces to /auth. Auth/Amplify sign-outs handled below; we don't call
    // them here because the user might be unauthenticated already.
    if (isInactivityExpired()) {
      clearActivity()
      clearProfileSnapshot()
      tokenStorage.clearTokens()
      try { await signOut() } catch { /* not signed in / network issue — ignore */ }
      setUser(null)
      setLoading(false)
      return
    }

    // Try Amplify first (Google OAuth users have a session there)
    try {
      const cognitoUser = await getCurrentUser()
      const session = await fetchAuthSession()
      const accessToken = session.tokens?.accessToken?.toString()
      const idToken = session.tokens?.idToken?.toString()
      if (accessToken && idToken) {
        tokenStorage.setToken(accessToken)
        tokenStorage.setIdToken(idToken)
        // Mark activity any time Amplify produces fresh tokens — this is
        // either an initial OAuth sign-in or a Cognito-initiated refresh.
        // Either way the user is actively here.
        recordActivity()
      }
      if (cognitoUser) {
        try {
          const userData = await profileApi.getProfile()
          applyUser(userData)
          setLoading(false)
          return
        } catch (err) {
          if (isOfflineError(err)) {
            // Network gone, not auth gone: keep tokens, serve the snapshot.
            setUser(readProfileSnapshot())
            setLoading(false)
            return
          }
          tokenStorage.clearTokens()
          clearProfileSnapshot()
          try { await signOut() } catch { /* ignore */ }
        }
      }
    } catch {
      // No Amplify session — try OTP tokens.
    }

    // OTP flow: tokens stored directly in localStorage by verifyOTP.
    const token = tokenStorage.getToken()
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const userData = await profileApi.getProfile()
      applyUser(userData)
    } catch (err) {
      if (isOfflineError(err)) {
        setUser(readProfileSnapshot())
      } else {
        tokenStorage.clearTokens()
        clearProfileSnapshot()
        setUser(null)
      }
    }
    setLoading(false)
  }, [applyUser])

  useEffect(() => {
    checkAuth()
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        checkAuth()
      }
      if (payload.event === 'signInWithRedirect_failure') {
        // payload.data can include the raw OAuth error and state echo;
        // keep it out of production consoles. UI surfaces a generic message.
        if (import.meta.env.DEV) console.error('OAuth sign-in failed:', payload.data)
        setError(i18n.t('auth.googleSignInFailed'))
        setLoading(false)
      }
      if (payload.event === 'signedOut') {
        clearProfileSnapshot()
        setUser(null)
      }
    })
    return unsubscribe
  }, [checkAuth])

  // Listen for unrecoverable 401s from lib/api.ts. Clearing `user` is enough:
  // every protected route re-renders and ProtectedLayoutRoute returns
  // <Navigate to="/auth" />. We deliberately don't call useNavigate() here —
  // doing so couples AuthProvider to the router and has caused issues during
  // hot-reload where the router context wasn't yet hydrated at AuthProvider
  // render time. The Navigate-from-route pattern is idiomatic for v6.
  useEffect(() => {
    const handler = () => {
      tokenStorage.clearTokens()
      // Clear the inactivity clock too — the next session starts fresh
      // rather than inheriting a stale "last activity" from before the
      // expiry.
      clearActivity()
      clearProfileSnapshot()
      setUser(null)
      // Drop any cached API responses tied to the now-defunct session — same
      // rationale as logout(). Fire-and-forget: cache deletion is async but
      // we don't gate the React state update on it.
      void invalidateApiCache()
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handler)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler)
  }, [])

  // All transition callbacks below are wrapped in useCallback. They take no
  // props and only call setters / module-level helpers, so the dependency
  // arrays are stable empty arrays and the references survive across renders.
  // This matters for useMemo on `value` (below) and also lets consumers safely
  // pass these into other useEffect deps without triggering re-runs.

  const login = useCallback(async (identifier: string, method: AuthMethod): Promise<string> => {
    try {
      setError(null)
      const response = await authApi.login({ identifier, method })
      return response.session || ''
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(i18n.t('auth.unexpectedError'))
      }
      throw err
    }
  }, [])

  const verifyOTP = useCallback(async (
    identifier: string,
    code: string,
    session: string,
    method: AuthMethod
  ) => {
    try {
      setError(null)
      setLoading(true)
      const response = await authApi.verifyOTP({ identifier, code, session, method })
      if (response.tokens) {
        tokenStorage.setToken(response.tokens.accessToken)
        tokenStorage.setIdToken(response.tokens.idToken)
        tokenStorage.setRefreshToken(response.tokens.refreshToken)
        // Successful login starts the rolling inactivity clock at now.
        recordActivity()
      }
      if (response.user) {
        applyUser(response.user)
      } else {
        const userData = await profileApi.getProfile()
        applyUser(userData)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(i18n.t('auth.unexpectedError'))
      }
      throw err
    } finally {
      setLoading(false)
    }
  }, [applyUser])

  const loginWithGoogle = useCallback(async () => {
    try {
      setError(null)
      setLoading(true)
      try { await signOut() } catch { /* not signed in */ }
      await signInWithRedirect({ provider: 'Google' })
    } catch (err) {
      if (import.meta.env.DEV) console.error('signInWithRedirect threw:', err)
      // Surface the localized headline; the underlying provider message is
      // dev-only (it can include redirect URIs / state) and never reaches
      // the production console.
      setError(i18n.t('auth.googleSignInFailed'))
      setLoading(false)
      throw err
    }
  }, [])

  const register = useCallback(async (data: { name: string; email?: string; phoneNumber?: string }) => {
    try {
      setError(null)
      setLoading(true)
      await authApi.register(data)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(i18n.t('auth.unexpectedError'))
      }
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => { /* best-effort */ })
    clearInFlightGets()
    tokenStorage.clearTokens()
    clearActivity()
    clearProfileSnapshot()
    setUser(null)
    setError(null)
    // Drop the SW's per-user API cache so the next sign-in (possibly a
    // different account on the same browser) doesn't see the previous user's
    // /profile, /playlists, /bookmarks payloads while SWR's revalidate is in
    // flight. This is a privacy fix, not just a UX one — without it cross-user
    // PII bleeds for the duration of the first network round-trip.
    await invalidateApiCache()
    try { await signOut() } catch { /* ignore */ }
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const userData = await profileApi.getProfile()
      applyUser(userData)
    } catch {
      // Silently fail
    }
  }, [applyUser])

  // Memoise the context value so consumers don't re-render on every parent
  // render. Without this, a single setUser (e.g. games-record's response push)
  // would create a fresh value object and fan re-renders through every
  // component reading useAuth — which is most of the app. The deps include
  // every reactive piece + the stable callbacks so a real change still
  // propagates.
  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    error,
    login,
    verifyOTP,
    loginWithGoogle,
    register,
    logout,
    refreshUser,
    setUser: applyUser,
    isAuthenticated: !!user,
  }), [user, loading, error, login, verifyOTP, loginWithGoogle, register, logout, refreshUser, applyUser])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

